import { useState } from 'react';
import {
  Heart,
  Eye,
  MessageCircle,
  Share2,
  MapPin,
  UserPlus
} from 'lucide-react';
import { Card, Badge } from 'react-bootstrap';
import Button from '../UI/button';
import publicTripService from '../../services/public-trip.service';

export interface IPublicTrip {
  id: string;
  // Flattened trip data (from summary endpoint)
  title?: string;
  description?: string;
  imageUrl?: string;
  destinationNames?: string[];
  destinationNamesDisplay?: string;
  destinationCount?: number;
  activityCount?: number;
  startDate?: string;
  endDate?: string;

  // Legacy nested structure (for backward compatibility)
  tripSnapshot?: {
    title: string;
    description?: string;
    dateRange?: {
      startDate: string;
      endDate: string;
    };
    destinations?: Array<{
      name: string;
      placeId?: string;
    }>;
    activities?: any[];
    imageUrl?: string;
  };

  // Metadata
  publishDate: string;
  viewCount: number;
  likesCount: number;
  commentsCount: number;
  shareCount: number;
  isLikedByUser?: boolean;
}

type PublicTripCardProps = {
  publicTrip: IPublicTrip;
  onClick: () => void;
  onLikeToggle?: (publicTripId: string, isLiked: boolean) => void;
  onShare?: () => void;
};

function capitalizeFirstLetter(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function PublicTripCard({
  publicTrip,
  onClick,
  onLikeToggle,
  onShare
}: PublicTripCardProps) {
  const [imageError, setImageError] = useState(false);
  const [isLiked, setIsLiked] = useState(publicTrip.isLikedByUser || false);
  const [likesCount, setLikesCount] = useState(publicTrip.likesCount);

  const formattedDate = publicTrip.publishDate
    ? new Date(publicTrip.publishDate).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      })
    : '';

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only trigger onClick if the click target is the card itself, not a button or interactive element
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('.card-body-interactions')) {
      return;
    }
    onClick();
  };

  const handleLikeClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const previousLiked = isLiked;
    const previousCount = likesCount;

    // Optimistic update
    setIsLiked(!isLiked);
    setLikesCount(isLiked ? likesCount - 1 : likesCount + 1);

    try {
      // Make API call
      if (isLiked) {
        await publicTripService.unlikePublicTrip(publicTrip.id);
      } else {
        await publicTripService.likePublicTrip(publicTrip.id);
      }

      // Notify parent of the state change (optional callback)
      if (onLikeToggle) {
        onLikeToggle(publicTrip.id, !isLiked);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revert on error
      setIsLiked(previousLiked);
      setLikesCount(previousCount);
    }
  };

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onShare) {
      onShare();
    }
  };

  const handleFollowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    // TODO: Implement follow logic
    const confirmed = window.confirm('Follow this trip?');
    if (confirmed) {
      console.log('User wants to follow trip:', publicTrip.id);
      // Future: Implement actual follow logic here
    }
  };

  // Support both flattened and nested structure
  const destinationsList =
    publicTrip.destinationNamesDisplay ||
    publicTrip.destinationNames?.slice(0, 2).join(', ') ||
    publicTrip.tripSnapshot?.destinations
      ?.slice(0, 2)
      .map((dest) => dest.name)
      .join(', ');

  const activitiesCount =
    publicTrip.activityCount ??
    publicTrip.tripSnapshot?.activities?.length ??
    0;

  return (
    <Card
      className='shadow-sm cursor-pointer public-trip-card h-100'
      style={{ transition: 'box-shadow 0.2s' }}
      onMouseEnter={(e) => e.currentTarget.classList.add('shadow')}
      onMouseLeave={(e) => e.currentTarget.classList.remove('shadow')}
    >
      <div
        onClick={handleCardClick}
        className='position-relative bg-light'
        style={{ height: '260px', cursor: 'pointer' }}
      >
        {(publicTrip.imageUrl || publicTrip.tripSnapshot?.imageUrl) &&
        !imageError ? (
          <img
            src={publicTrip.imageUrl || publicTrip.tripSnapshot?.imageUrl || ''}
            alt={publicTrip.title || publicTrip.tripSnapshot?.title || 'Trip'}
            className='w-100 h-100'
            style={{ objectFit: 'cover' }}
            onError={() => setImageError(true)}
          />
        ) : (
          <div className='w-100 h-100 d-flex align-items-center justify-content-center text-muted'>
            No image
          </div>
        )}

        {/* View Count Badge */}
        {publicTrip.viewCount > 0 && (
          <div className='position-absolute top-0 end-0 m-3'>
            <Badge
              bg='dark'
              className='rounded-pill d-flex align-items-center gap-1 px-2 py-1'
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                fontSize: '0.75rem'
              }}
            >
              <Eye size={12} />
              <span>{publicTrip.viewCount}</span>
            </Badge>
          </div>
        )}

        {/* Trip Info Overlay */}
        <div
          className='position-absolute bottom-0 start-0 end-0 p-3'
          style={{
            background:
              'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 75%, rgba(0, 0, 0, 0.5) 100%)'
          }}
        >
          <div className='d-flex align-items-start justify-content-between gap-2'>
            <div className='flex-fill'>
              <h5 className='text-white mb-1'>
                {capitalizeFirstLetter(
                  publicTrip.title || publicTrip.tripSnapshot?.title || ''
                )}
              </h5>
              {destinationsList && (
                <div className='d-flex align-items-center gap-1 text-white mb-1'>
                  <MapPin
                    size={14}
                    style={{ opacity: 0.9 }}
                  />
                  <small style={{ opacity: 0.9 }}>{destinationsList}</small>
                </div>
              )}
              {formattedDate && (
                <small
                  className='text-white'
                  style={{ opacity: 0.8 }}
                >
                  Published {formattedDate}
                </small>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Card Body with Interactions */}
      <Card.Body
        className='p-2 card-body-interactions'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='d-flex align-items-center justify-content-between'>
          <div className='d-flex align-items-center gap-3'>
            {/* Like Button */}
            <Button
              onClick={handleLikeClick}
              variant='transparent'
              className='p-0 d-flex align-items-center gap-1'
              title={isLiked ? 'Unlike trip' : 'Like trip'}
            >
              <Heart
                size={16}
                className={isLiked ? 'text-danger' : 'text-muted'}
                fill={isLiked ? 'currentColor' : 'none'}
              />
              {likesCount > 0 && (
                <small className='text-muted'>{likesCount}</small>
              )}
            </Button>

            {/* Follow Button */}
            <Button
              onClick={handleFollowClick}
              variant='transparent'
              className='p-0 d-flex align-items-center gap-1'
              title='Follow trip'
            >
              <UserPlus
                size={16}
                className='text-muted'
              />
            </Button>

            {/* Comments Count */}
            {publicTrip.commentsCount > 0 && (
              <div className='d-flex align-items-center gap-1'>
                <MessageCircle
                  size={16}
                  className='text-muted'
                />
                <small className='text-muted'>{publicTrip.commentsCount}</small>
              </div>
            )}

            {/* Activities Count */}
            {activitiesCount > 0 && (
              <small className='text-muted'>
                {activitiesCount}{' '}
                {activitiesCount === 1 ? 'activity' : 'activities'}
              </small>
            )}
          </div>

          {/* Share Button */}
          <Button
            onClick={handleShareClick}
            variant='transparent'
            height={20}
            width={20}
            className='p-0'
            title='Share trip'
          >
            <Share2
              size={16}
              className='text-primary'
            />
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}
